/**
 * ScrollReveal Component
 *
 * Animates children when they scroll into view using Intersection Observer.
 * Supports multiple reveal patterns and stagger effects.
 */

import React from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  fadeUp,
  fadeDown,
  fadeLeft,
  fadeRight,
  scaleFade,
  zoomIn,
  scrollViewport,
  scrollViewportImmediate,
} from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

type RevealPattern =
  | 'fadeUp'
  | 'fadeDown'
  | 'fadeLeft'
  | 'fadeRight'
  | 'scaleFade'
  | 'zoomIn';

interface ScrollRevealProps {
  children: React.ReactNode;
  /**
   * Animation pattern to use
   */
  pattern?: RevealPattern;
  /**
   * Custom variants (overrides pattern)
   */
  variants?: Variants;
  /**
   * Delay before animation starts (in seconds)
   */
  delay?: number;
  /**
   * Viewport amount required to trigger (0-1)
   */
  amount?: number;
  /**
   * Whether to trigger immediately or wait for more scroll
   */
  immediate?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * HTML element type to render
   */
  as?: keyof JSX.IntrinsicElements;
}

const patternMap: Record<RevealPattern, Variants> = {
  fadeUp,
  fadeDown,
  fadeLeft,
  fadeRight,
  scaleFade,
  zoomIn,
};

/**
 * ScrollReveal component
 *
 * Reveals content with animation when scrolled into view.
 * Respects user motion preferences and only animates once by default.
 *
 * @example Basic fade up
 * <ScrollReveal>
 *   <div>This will fade up when scrolled into view</div>
 * </ScrollReveal>
 *
 * @example With custom pattern and delay
 * <ScrollReveal pattern="scaleFade" delay={0.2}>
 *   <Card />
 * </ScrollReveal>
 *
 * @example With custom variants
 * <ScrollReveal variants={customVariants}>
 *   <Content />
 * </ScrollReveal>
 */
export function ScrollReveal({
  children,
  pattern = 'fadeUp',
  variants: customVariants,
  delay = 0,
  amount,
  immediate = false,
  className = '',
  as = 'div',
}: ScrollRevealProps) {
  const { getVariants } = useAnimationState();
  const Component = motion[as] as typeof motion.div;

  // Use custom variants or pattern variants
  const baseVariants = customVariants || patternMap[pattern];
  const variants = getVariants(baseVariants);

  // Viewport configuration
  const viewport = {
    ...(immediate ? scrollViewportImmediate : scrollViewport),
    ...(amount !== undefined && { amount }),
  };

  // Add delay to visible transition if specified
  const variantsWithDelay = delay > 0
    ? {
        ...variants,
        visible: {
          ...(typeof variants.visible === 'object' ? variants.visible : {}),
          transition: {
            ...(typeof variants.visible === 'object' && 'transition' in variants.visible
              ? variants.visible.transition
              : {}),
            delay,
          },
        },
      }
    : variants;

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      variants={variantsWithDelay}
      className={`${className} opacity-0`}
    >
      {children}
    </Component>
  );
}

/**
 * ScrollRevealList - For revealing list items with stagger
 *
 * @example
 * <ScrollRevealList>
 *   {items.map(item => (
 *     <div key={item.id}>{item.content}</div>
 *   ))}
 * </ScrollRevealList>
 */
export function ScrollRevealList({
  children,
  staggerDelay = 0.1,
  pattern = 'fadeUp',
  className = '',
  as = 'div',
}: Omit<ScrollRevealProps, 'variants'> & {
  staggerDelay?: number;
}) {
  const { getVariants } = useAnimationState();
  const Component = motion[as] as typeof motion.div;

  const containerVariants = getVariants({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  });

  const itemVariants = getVariants(patternMap[pattern]);

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={scrollViewport}
      variants={containerVariants}
      className={className}
    >
      {React.Children.map(children, (child) => (
        <motion.div variants={itemVariants}>{child}</motion.div>
      ))}
    </Component>
  );
}
